import React from 'react';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { Status } from '../../../../types/redux/data/dataTypes';
import {
  formatESGWithUnit,
  percentageToNdecialPlaces,
} from '../../../../utilities/numberFormatters';
import RaptorStatusBox from '../../../feedback/RaptorStatusBox.component';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import { IndicatorCoverageRatioMonitorTableData } from './IndictaorCoverageRatioMonitor.component';
import IndicatorCoverageRatioSecondLevelTable from './IndicatorCoverageRatioMonitorSecondLevelTable.component';
import makeStyles from '@mui/styles/makeStyles';
import { mainColors } from '../../../../styling/theme';
import type { IndicatorCoverageRatioPayload } from '../../../../api/rs-hg/esg';

interface IndictaorCoverageRatioMonitorTableProps {
  data: IndicatorCoverageRatioPayload;
}

const useStyles = makeStyles(() => ({
  tabsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '1.25rem',
    width: '100%',
  },
  tab_active: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 1.25rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '1.12rem',
    backgroundColor: mainColors.mainBlue,
    color: 'white',
    cursor: 'pointer',
    userSelect: 'none',
  },
  tab_inactive: {
    all: 'unset',
    width: 'fit-content',
    height: '1.88rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 1.25rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '1.12rem',
    backgroundColor: 'white',
    color: mainColors.mainBlue,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: mainColors.hoverOverWhite,
    },
  },
}));

const standardColumns: CustomColumn<IndicatorCoverageRatioMonitorTableData>[] =
  [
    {
      title: 'Indicator',
      field: 'indicator',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Name',
      field: 'indicator_name',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Description',
      field: 'description',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      width: '25%',
    },
    {
      title: 'Portfolio Indicator Value',
      field: 'value',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => formatESGWithUnit(rowData.value, rowData.unit),
    },
    {
      title: 'Total Gross Exposure %',
      field: 'total_gross_exposure_percentage',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        percentageToNdecialPlaces(rowData.total_gross_exposure_percentage, 2),
    },
    {
      title: 'Total Coverage Exposure %',
      field: 'total_covered_exposure_percentage',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        percentageToNdecialPlaces(rowData.total_covered_exposure_percentage, 2),
    },
    {
      title: 'Coverage Ratio',
      field: 'coverage_ratio_percentage',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) =>
        percentageToNdecialPlaces(rowData.coverage_ratio_percentage, 2),
    },
    {
      title: 'Limit',
      field: 'limit',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => percentageToNdecialPlaces(rowData.limit, 2),
    },
    {
      title: 'Limit Type',
      field: 'limit_type',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Status',
      field: 'pass_fail',
      cellStyle: {
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <RaptorStatusBox status={rowData.pass_fail as Status} />
      ),
    },
  ];

const buildColumns = (
  data: IndicatorCoverageRatioPayload,
  columns: CustomColumn<IndicatorCoverageRatioMonitorTableData>[],
): CustomColumn<IndicatorCoverageRatioMonitorTableData>[] => {
  if (!data.configs?.indicator_columns) {
    return columns;
  }

  const returnColumns: CustomColumn<IndicatorCoverageRatioMonitorTableData>[] =
    [];
  data.configs.indicator_columns.forEach((column: string) => {
    columns.forEach(
      (col: CustomColumn<IndicatorCoverageRatioMonitorTableData>) => {
        if (col.field === column) {
          returnColumns.push(col);
        }
      },
    );
  });
  return returnColumns;
};

const buildTableData = (
  data: IndicatorCoverageRatioPayload,
  tab: string,
): IndicatorCoverageRatioMonitorTableData[] => {
  if (
    !data.data?.length ||
    !data.configs.indicators ||
    !data.configs.indicators[tab]
  ) {
    return [];
  }

  const indicators = data.configs.indicators[tab];
  const returnData: IndicatorCoverageRatioMonitorTableData[] = [];
  data.data.forEach((row) => {
    if (
      Object.keys(row).includes('indicator') &&
      indicators.includes(row.indicator)
    ) {
      returnData.push(row as IndicatorCoverageRatioMonitorTableData);
    }
  });
  return returnData;
};

const IndicatorCoverateMonitorTable: React.FC<
  IndictaorCoverageRatioMonitorTableProps
> = ({ data }) => {
  const classes = useStyles();

  const [tabs] = React.useState<string[]>(
    Object.keys(data.configs.indicators),
  );
  const [activeTab, setActiveTab] = React.useState<string>(
    Object.keys(data.configs.indicators)[0],
  );

  return (
    <GridItem xs={12} card>
      <CustomTable<IndicatorCoverageRatioMonitorTableData>
        columns={buildColumns(data, standardColumns)}
        showToolbar
        data={buildTableData(data, activeTab)}
        title={
          tabs.length > 1 ? (
            <div className={classes.tabsContainer}>
              {Object.keys(data.configs.indicators).map((tab: string) => {
                return (
                  <div
                    key={tab}
                    className={
                      activeTab === tab
                        ? classes.tab_active
                        : classes.tab_inactive
                    }
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </div>
                );
              })}
            </div>
          ) : (
            activeTab
          )
        }
        options={{
          draggable: false,
          sorting: false,
          paging: false,
          search: true,
          exportButton: true,
        }}
        detailPanel={[
          {
            disabled: false,
            render: (rowData: IndicatorCoverageRatioMonitorTableData) => {
              return (
                <IndicatorCoverageRatioSecondLevelTable
                  data={data}
                  rowData={rowData}
                />
              );
            },
          },
        ]}
      />
    </GridItem>
  );
};

export default IndicatorCoverateMonitorTable;
